import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { ArrowDown, ArrowLeft, ArrowUp, Plus, Route as RouteIcon } from 'lucide-react';
import { Button } from '@/components/common/Button';
import { Card } from '@/components/common/Card';
import { Modal } from '@/components/common/Modal';
import { TextField } from '@/components/forms/Field';
import { PageHeader } from '@/components/layout/PageHeader';
import { ErrorState } from '@/components/common/States';
import { Spinner } from '@/components/common/Spinner';
import { useToast } from '@/components/feedback/ToastProvider';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { useCampusPicker } from '@/hooks/usePickers';
import {
  transportDriversApi,
  transportRoutesApi,
  vehiclesApi,
} from '@/services/api/endpoints/transport';
import { queryKeys } from '@/services/api/queryKeys';
import { useAuth } from '@/features/auth/useAuth';
import { PERMISSIONS } from '@/types/permissions';
import { ROUTES } from '@/app/router/routes';
import type { UUID } from '@/types/domain';
import type {
  DriverRole,
  RouteDirection,
  RouteStop,
  TransportStatus,
} from '@/types/transport';
import { orDash } from '@/utils/format';
import {
  Badge,
  Blockers,
  Field,
  RouteStatusBadge,
  Stat,
  StopSequence,
  formatTime,
} from './shared';

/**
 * One route: its stops in running order, who serves it, and the capacity
 * arithmetic.
 *
 * <p>The stops are a numbered sequence, never a map (§37) — a driver reads a
 * list. Reordering sends the complete new order and the server renumbers
 * densely; nothing here computes a sequence, and a stop with students boarding
 * at it cannot be removed or have that purpose switched off — the refusal
 * arrives from the server in its own words.
 *
 * <p>Every capacity figure — budget, seats, effective, occupied — arrives
 * computed (§38). Assigning a vehicle or driver closes the previous assignment
 * rather than overwriting it, so who served the route stays answerable; the
 * route record itself carries only the open vehicle and driver, and does not
 * name the open attendant.
 */

/** Same map as the listing. "dropoff" is not a word a parent reads. */
const DIRECTION_LABEL: Record<RouteDirection, string> = {
  PICKUP: 'Pickup only',
  DROPOFF: 'Drop-off only',
  BOTH: 'Both ways',
};

type Slot = 'vehicle' | 'driver' | 'attendant';

const SLOT_TITLE: Record<Slot, string> = {
  vehicle: 'Assign a vehicle',
  driver: 'Assign a driver',
  attendant: 'Assign an attendant',
};

export function RouteDetailPage() {
  const { id = '' } = useParams();
  const { can } = useAuth();
  const toast = useToast();
  const handleApiError = useApiErrorHandler();
  const queryClient = useQueryClient();

  const managesRoute = can(PERMISSIONS.TRANSPORT_ROUTE_MANAGE);
  const managesAssignments = can(PERMISSIONS.TRANSPORT_ASSIGNMENT_MANAGE);

  // ---- route edit modal ----------------------------------------------------
  const [editing, setEditing] = useState(false);
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [campusId, setCampusId] = useState('');
  const [direction, setDirection] = useState<RouteDirection>('BOTH');
  const [startLocation, setStartLocation] = useState('');
  const [endLocation, setEndLocation] = useState('');
  const [duration, setDuration] = useState('');
  const [distance, setDistance] = useState('');
  const [capacity, setCapacity] = useState('');
  const [notes, setNotes] = useState('');

  // ---- stop modal ----------------------------------------------------------
  const [stopOpen, setStopOpen] = useState(false);
  const [stopEditing, setStopEditing] = useState<RouteStop | null>(null);
  const [stopName, setStopName] = useState('');
  const [stopAddress, setStopAddress] = useState('');
  const [pickupTime, setPickupTime] = useState('');
  const [dropOffTime, setDropOffTime] = useState('');
  const [allowsPickup, setAllowsPickup] = useState(true);
  const [allowsDropOff, setAllowsDropOff] = useState(true);
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');
  const [stopNotes, setStopNotes] = useState('');

  // ---- assignment modals ---------------------------------------------------
  const [assigning, setAssigning] = useState<Slot | null>(null);
  const [pickSearch, setPickSearch] = useState('');
  const [pickId, setPickId] = useState('');
  const [pickFrom, setPickFrom] = useState('');
  const [ending, setEnding] = useState<Slot | null>(null);
  const [endReason, setEndReason] = useState('');

  const campuses = useCampusPicker();

  const route = useQuery({
    queryKey: queryKeys.transportRoutes.detail(id),
    queryFn: () => transportRoutesApi.get(id),
  });

  /*
   * The pickers are a searched page, not the whole fleet — the same §44 rule
   * as every listing. Fifty active rows is a page a coordinator can read;
   * narrowing happens on the server through the search box.
   */
  const pickerSearch = useDebouncedValue(pickSearch, 300);
  const vehicleParams = {
    page: 0,
    size: 50,
    status: 'ACTIVE' as const,
    ...(pickerSearch.trim() === '' ? {} : { search: pickerSearch.trim() }),
  };
  const vehicleOptions = useQuery({
    queryKey: queryKeys.vehicles.list(vehicleParams),
    queryFn: () => vehiclesApi.list(vehicleParams),
    enabled: assigning === 'vehicle',
  });

  const staffRole: DriverRole = assigning === 'attendant' ? 'ATTENDANT' : 'DRIVER';
  const staffParams = {
    page: 0,
    size: 50,
    status: 'ACTIVE' as const,
    role: staffRole,
    ...(pickerSearch.trim() === '' ? {} : { search: pickerSearch.trim() }),
  };
  const staffOptions = useQuery({
    queryKey: queryKeys.transportDrivers.list(staffParams),
    queryFn: () => transportDriversApi.list(staffParams),
    enabled: assigning === 'driver' || assigning === 'attendant',
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.transportRoutes.all });
    void queryClient.invalidateQueries({ queryKey: queryKeys.transport.all });
  };

  function closeAssign() {
    setAssigning(null);
    setPickSearch('');
    setPickId('');
    setPickFrom('');
  }

  function closeEnd() {
    setEnding(null);
    setEndReason('');
  }

  // ---- route mutations -----------------------------------------------------

  const saveRoute = useMutation({
    mutationFn: () =>
      transportRoutesApi.update(id, {
        code: code.trim(),
        name: name.trim(),
        campusId: campusId === '' ? null : campusId,
        direction,
        startLocation: startLocation.trim() === '' ? undefined : startLocation.trim(),
        endLocation: endLocation.trim() === '' ? undefined : endLocation.trim(),
        estimatedDurationMinutes: duration === '' ? undefined : Number(duration),
        distanceKm: distance === '' ? undefined : Number(distance),
        capacity: Number(capacity),
        notes: notes.trim() === '' ? undefined : notes.trim(),
      }),
    onSuccess: (updated) => {
      toast.success(`${updated.displayName} updated`);
      setEditing(false);
      invalidate();
    },
    onError: (error) => handleApiError(error),
  });

  const setStatus = useMutation({
    mutationFn: (next: TransportStatus) => transportRoutesApi.setStatus(id, next),
    onSuccess: (updated) => {
      toast.success(
        updated.status === 'ACTIVE'
          ? 'The route is running again'
          : 'Route deactivated — stops and history kept',
      );
      invalidate();
    },
    // Deactivating under enrolled students is refused; the reason shows here.
    onError: (error) => handleApiError(error),
  });

  // ---- stop mutations ------------------------------------------------------

  const saveStop = useMutation({
    mutationFn: () => {
      const payload = {
        name: stopName.trim(),
        address: stopAddress.trim() === '' ? undefined : stopAddress.trim(),
        latitude: latitude.trim() === '' ? undefined : Number(latitude),
        longitude: longitude.trim() === '' ? undefined : Number(longitude),
        pickupTime: pickupTime === '' ? undefined : pickupTime,
        dropOffTime: dropOffTime === '' ? undefined : dropOffTime,
        allowsPickup,
        allowsDropOff,
        notes: stopNotes.trim() === '' ? undefined : stopNotes.trim(),
      };
      return stopEditing === null
        ? transportRoutesApi.addStop(id, payload)
        : transportRoutesApi.updateStop(id, stopEditing.id, payload);
    },
    onSuccess: (stop) => {
      toast.success(`${stop.name} ${stopEditing === null ? 'added' : 'updated'}`);
      setStopOpen(false);
      invalidate();
    },
    onError: (error) => handleApiError(error),
  });

  const removeStop = useMutation({
    mutationFn: (stopId: UUID) => transportRoutesApi.removeStop(id, stopId),
    onSuccess: () => {
      toast.success('Stop removed — the rest renumber');
      invalidate();
    },
    // Refused while students board or alight there; the reason shows here.
    onError: (error) => handleApiError(error),
  });

  const reorder = useMutation({
    // Every stop id in the new order — the server refuses a partial list.
    mutationFn: (orderedStopIds: UUID[]) => transportRoutesApi.reorderStops(id, orderedStopIds),
    onSuccess: () => {
      toast.success('Stops reordered');
      invalidate();
    },
    onError: (error) => handleApiError(error),
  });

  // ---- assignment mutations ------------------------------------------------

  const assignVehicle = useMutation({
    mutationFn: (vars: { vehicleId: UUID; from?: string }) =>
      transportRoutesApi.assignVehicle(id, vars.vehicleId, vars.from),
    onSuccess: (updated) => {
      toast.success(`${updated.vehicleName ?? 'The vehicle'} now serves ${updated.displayName}`);
      closeAssign();
      invalidate();
      void queryClient.invalidateQueries({ queryKey: queryKeys.vehicles.all });
    },
    // A benched vehicle, a double booking, too few seats — the server's words.
    onError: (error) => handleApiError(error),
  });

  const assignStaff = useMutation({
    // The role is taken from the person's own profile, so one call serves
    // both the driver and the attendant panels.
    mutationFn: (vars: { driverId: UUID; from?: string }) =>
      transportRoutesApi.assignDriver(id, vars.driverId, vars.from),
    onSuccess: (updated) => {
      toast.success(
        assigning === 'attendant'
          ? 'Attendant assigned'
          : `${updated.driverName ?? 'The driver'} now serves ${updated.displayName}`,
      );
      closeAssign();
      invalidate();
      void queryClient.invalidateQueries({ queryKey: queryKeys.transportDrivers.all });
    },
    onError: (error) => handleApiError(error),
  });

  const endVehicle = useMutation({
    mutationFn: (reason?: string) => transportRoutesApi.unassignVehicle(id, reason),
    onSuccess: () => {
      toast.success('Assignment ended — it stays on the record');
      closeEnd();
      invalidate();
      void queryClient.invalidateQueries({ queryKey: queryKeys.vehicles.all });
    },
    onError: (error) => handleApiError(error),
  });

  const endStaff = useMutation({
    mutationFn: (vars: { role: DriverRole; reason?: string }) =>
      transportRoutesApi.unassignDriver(id, vars.role, vars.reason),
    onSuccess: () => {
      toast.success('Assignment ended — it stays on the record');
      closeEnd();
      invalidate();
      void queryClient.invalidateQueries({ queryKey: queryKeys.transportDrivers.all });
    },
    onError: (error) => handleApiError(error),
  });

  // ---- rendering -----------------------------------------------------------

  if (route.isError) {
    return <ErrorState error={route.error} onRetry={() => void route.refetch()} />;
  }
  if (route.isPending) {
    return (
      <div className="flex justify-center py-16">
        <Spinner />
      </div>
    );
  }

  const data = route.data;
  const stops = data.stops;

  function openEdit() {
    setCode(data.code);
    setName(data.name);
    setCampusId(data.campusId ?? '');
    setDirection(data.direction);
    setStartLocation(data.startLocation ?? '');
    setEndLocation(data.endLocation ?? '');
    setDuration(data.estimatedDurationMinutes === undefined ? '' : String(data.estimatedDurationMinutes));
    setDistance(data.distanceKm === undefined ? '' : String(data.distanceKm));
    setCapacity(String(data.capacity));
    setNotes(data.notes ?? '');
    setEditing(true);
  }

  function openStop(stop: RouteStop | null) {
    setStopEditing(stop);
    setStopName(stop?.name ?? '');
    setStopAddress(stop?.address ?? '');
    setPickupTime(stop?.pickupTime === undefined ? '' : stop.pickupTime.slice(0, 5));
    setDropOffTime(stop?.dropOffTime === undefined ? '' : stop.dropOffTime.slice(0, 5));
    setAllowsPickup(stop?.allowsPickup ?? true);
    setAllowsDropOff(stop?.allowsDropOff ?? true);
    setLatitude(stop?.latitude === undefined ? '' : String(stop.latitude));
    setLongitude(stop?.longitude === undefined ? '' : String(stop.longitude));
    setStopNotes(stop?.notes ?? '');
    setStopOpen(true);
  }

  /** Swap two neighbours and send the whole order — no sequence maths here. */
  function move(index: number, delta: number) {
    const target = index + delta;
    if (target < 0 || target >= stops.length) {
      return;
    }
    const ids = stops.map((stop) => stop.id);
    const moved = ids[index];
    const other = ids[target];
    if (moved === undefined || other === undefined) {
      return;
    }
    ids[index] = other;
    ids[target] = moved;
    reorder.mutate(ids);
  }

  /** The one-line summary under a stop: purposes with their times, then place. */
  function stopMeta(stop: RouteStop): string {
    const parts: string[] = [];
    if (stop.allowsPickup) {
      parts.push(`up ${formatTime(stop.pickupTime)}`);
    }
    if (stop.allowsDropOff) {
      parts.push(`down ${formatTime(stop.dropOffTime)}`);
    }
    if (parts.length === 0) {
      parts.push('no boarding or alighting');
    }
    if (stop.address !== undefined) {
      parts.push(stop.address);
    }
    return parts.join(' · ');
  }

  const pickedStaff = (staffOptions.data?.data ?? []).find((row) => row.id === pickId);

  return (
    <div>
      <Link
        to={ROUTES.transportRoutes}
        className="mb-2 inline-flex items-center gap-1.5 text-sm text-muted hover:text-accent"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        Routes
      </Link>

      <PageHeader
        title={data.displayName}
        description={`${DIRECTION_LABEL[data.direction]}${
          data.campusName === undefined ? '' : ` · ${data.campusName}`
        }`}
        icon={<RouteIcon className="h-5 w-5" aria-hidden="true" />}
        action={
          <span className="flex flex-wrap items-center gap-2">
            {data.status !== 'ACTIVE' && <RouteStatusBadge status={data.status} />}
            {managesRoute && (
              <Button variant="secondary" onClick={openEdit}>
                Edit
              </Button>
            )}
            {managesRoute && (
              <Button
                variant="secondary"
                onClick={() => setStatus.mutate(data.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE')}
                disabled={setStatus.isPending}
              >
                {data.status === 'ACTIVE' ? 'Deactivate' : 'Activate'}
              </Button>
            )}
          </span>
        }
      />

      {/* The capacity panel: four figures the coordinator reads together,
          every one computed on the server. */}
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Stat label="Route budget" value={data.capacity} hint="the route's own seat limit" />
        <Stat
          label="Vehicle seats"
          value={data.vehicleSeats ?? '—'}
          hint={data.vehicleSeats === undefined ? 'no vehicle assigned' : data.vehicleName}
        />
        <Stat
          label="Effective capacity"
          value={data.effectiveCapacity}
          hint="the smaller of budget and seats"
        />
        <Stat
          label="Occupied"
          value={`${data.assignedStudents} of ${data.effectiveCapacity}`}
          hint={`${data.availableSeats} free · ${data.utilizationPercent}% used`}
          tone={data.availableSeats === 0 ? 'danger' : 'neutral'}
        />
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]">
        <div className="space-y-4">
          <Card className="p-4">
            <h2 className="text-sm font-extrabold uppercase tracking-[0.05em] text-ink">Details</h2>
            <dl className="mt-2">
              <Field label="Code">
                <span className="font-mono">{data.code}</span>
              </Field>
              <Field label="Campus">{orDash(data.campusName)}</Field>
              <Field label="Direction">{DIRECTION_LABEL[data.direction]}</Field>
              <Field label="Starts at">{orDash(data.startLocation)}</Field>
              <Field label="Ends at">{orDash(data.endLocation)}</Field>
              <Field label="Duration">
                {data.estimatedDurationMinutes === undefined
                  ? '—'
                  : `${data.estimatedDurationMinutes} min`}
              </Field>
              <Field label="Distance">
                {data.distanceKm === undefined ? '—' : `${data.distanceKm} km`}
              </Field>
            </dl>
            {data.notes !== undefined && (
              <p className="mt-3 border-t border-line pt-3 text-sm text-muted">{data.notes}</p>
            )}
          </Card>

          <AssignmentPanel
            title="Vehicle"
            current={
              data.vehicleName === undefined ? (
                <span className="text-muted">No vehicle assigned.</span>
              ) : (
                <span>
                  <span className="font-semibold">{data.vehicleName}</span>
                  {data.vehicleSeats !== undefined && (
                    <span className="text-muted"> · {data.vehicleSeats} seats</span>
                  )}
                </span>
              )
            }
            note="Replacing closes the previous assignment rather than overwriting it — who served the route stays on the record."
            canManage={managesAssignments}
            assignLabel={data.vehicleName === undefined ? 'Assign' : 'Replace'}
            onAssign={() => setAssigning('vehicle')}
            onEnd={() => setEnding('vehicle')}
          />

          <AssignmentPanel
            title="Driver"
            current={
              data.driverName === undefined ? (
                <span className="text-muted">No driver assigned.</span>
              ) : (
                <span className="font-semibold">{data.driverName}</span>
              )
            }
            note="Eligibility is checked before the assignment lands; a refusal names the exact blockers."
            canManage={managesAssignments}
            assignLabel={data.driverName === undefined ? 'Assign' : 'Replace'}
            onAssign={() => setAssigning('driver')}
            onEnd={() => setEnding('driver')}
          />

          <AssignmentPanel
            title="Attendant"
            current={
              <span className="text-muted">
                The route record names only the driver, so the open attendant is not shown here.
              </span>
            }
            note="Assigning uses the person's own role; ending names the attendant slot. Either is refused with a reason when nothing fits."
            canManage={managesAssignments}
            assignLabel="Assign"
            onAssign={() => setAssigning('attendant')}
            onEnd={() => setEnding('attendant')}
          />
        </div>

        <Card className="p-4">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div>
              <h2 className="text-sm font-extrabold uppercase tracking-[0.05em] text-ink">Stops</h2>
              <p className="mt-0.5 text-xs text-muted">
                In the order the bus runs them. The numbering is the server's.
              </p>
            </div>
            {managesRoute && (
              <Button size="sm" onClick={() => openStop(null)}>
                <Plus className="h-4 w-4" aria-hidden="true" />
                Add a stop
              </Button>
            )}
          </div>

          {!managesRoute ? (
            <div className="mt-2">
              <StopSequence stops={stops} />
            </div>
          ) : stops.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted">
              No stops yet. A route without stops has nowhere to enrol a student.
            </p>
          ) : (
            <ol className="mt-2 divide-y divide-line">
              {stops.map((stop, index) => (
                <li key={stop.id} className="flex flex-wrap items-start gap-3 py-2.5">
                  <span
                    className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center border border-line bg-surface text-xs font-bold tabular-nums text-ink"
                    aria-hidden="true"
                  >
                    {stop.sequence}
                  </span>
                  <span className="min-w-0 flex-1 basis-40">
                    <span className="block text-sm font-semibold text-ink">
                      {stop.name}
                      {stop.status === 'INACTIVE' && (
                        <span className="ml-2 align-middle">
                          <Badge tone="neutral">inactive</Badge>
                        </span>
                      )}
                    </span>
                    <span className="block text-xs tabular-nums text-muted">{stopMeta(stop)}</span>
                  </span>
                  <span className="flex shrink-0 flex-wrap items-center gap-1">
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => move(index, -1)}
                      disabled={index === 0 || reorder.isPending}
                      aria-label={`Move ${stop.name} earlier`}
                    >
                      <ArrowUp className="h-4 w-4" aria-hidden="true" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => move(index, 1)}
                      disabled={index === stops.length - 1 || reorder.isPending}
                      aria-label={`Move ${stop.name} later`}
                    >
                      <ArrowDown className="h-4 w-4" aria-hidden="true" />
                    </Button>
                    <Button size="sm" variant="secondary" onClick={() => openStop(stop)}>
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => removeStop.mutate(stop.id)}
                      disabled={removeStop.isPending}
                    >
                      Remove
                    </Button>
                  </span>
                </li>
              ))}
            </ol>
          )}
        </Card>
      </div>

      {/* ---- edit route ----------------------------------------------------- */}
      <Modal
        open={editing}
        onClose={() => setEditing(false)}
        title={data.displayName}
        description="Capacity cannot go below the seats already held — the children exist, the network bends around them."
        footer={
          <>
            <Button variant="secondary" onClick={() => setEditing(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => saveRoute.mutate()}
              disabled={
                code.trim() === '' || name.trim() === '' || capacity === '' || saveRoute.isPending
              }
            >
              Save
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <TextField
              label="Code"
              value={code}
              onChange={(event) => setCode(event.target.value)}
              required
            />
            <TextField
              label="Seat budget"
              type="number"
              min={1}
              max={500}
              value={capacity}
              onChange={(event) => setCapacity(event.target.value)}
              required
            />
          </div>
          <TextField
            label="Name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            required
          />
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-[0.04em] text-muted">
              Campus
            </span>
            <select
              className="mt-1 w-full border border-line bg-bg px-3 py-2 text-sm text-ink"
              value={campusId}
              onChange={(event) => setCampusId(event.target.value)}
            >
              <option value="">Any campus</option>
              {(campuses.data ?? []).map((row) => (
                <option key={row.id} value={row.id}>
                  {row.name}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-[0.04em] text-muted">
              Direction
            </span>
            <select
              className="mt-1 w-full border border-line bg-bg px-3 py-2 text-sm text-ink"
              value={direction}
              onChange={(event) => setDirection(event.target.value as RouteDirection)}
            >
              {(['BOTH', 'PICKUP', 'DROPOFF'] as const).map((value) => (
                <option key={value} value={value}>
                  {DIRECTION_LABEL[value]}
                </option>
              ))}
            </select>
            <span className="mt-1.5 block text-xs text-subtle">
              Cannot be narrowed while riders depend on the half being removed.
            </span>
          </label>
          <div className="grid grid-cols-2 gap-3">
            <TextField
              label="Starts at"
              value={startLocation}
              onChange={(event) => setStartLocation(event.target.value)}
            />
            <TextField
              label="Ends at"
              value={endLocation}
              onChange={(event) => setEndLocation(event.target.value)}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <TextField
              label="Duration (min)"
              type="number"
              min={1}
              max={600}
              value={duration}
              onChange={(event) => setDuration(event.target.value)}
            />
            <TextField
              label="Distance (km)"
              type="number"
              min={0.1}
              step={0.1}
              value={distance}
              onChange={(event) => setDistance(event.target.value)}
            />
          </div>
          <TextField
            label="Notes"
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
          />
        </div>
      </Modal>

      {/* ---- add / edit stop ------------------------------------------------ */}
      <Modal
        open={stopOpen}
        onClose={() => setStopOpen(false)}
        title={stopEditing === null ? 'Add a stop' : stopEditing.name}
        description={
          stopEditing === null
            ? 'Added at the end of the route; move it with the arrows afterwards.'
            : 'A purpose with students using it cannot be switched off under them.'
        }
        footer={
          <>
            <Button variant="secondary" onClick={() => setStopOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => saveStop.mutate()}
              disabled={stopName.trim() === '' || saveStop.isPending}
            >
              Save
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <TextField
            label="Name"
            value={stopName}
            onChange={(event) => setStopName(event.target.value)}
            required
          />
          <TextField
            label="Address"
            value={stopAddress}
            onChange={(event) => setStopAddress(event.target.value)}
          />
          <div className="grid grid-cols-2 gap-3">
            <TextField
              label="Pickup time"
              type="time"
              value={pickupTime}
              onChange={(event) => setPickupTime(event.target.value)}
              hint="When the morning bus reaches it."
            />
            <TextField
              label="Drop-off time"
              type="time"
              value={dropOffTime}
              onChange={(event) => setDropOffTime(event.target.value)}
              hint="When the afternoon bus reaches it."
            />
          </div>
          <div className="space-y-1.5">
            <label className="flex items-center gap-2 text-sm text-ink">
              <input
                type="checkbox"
                checked={allowsPickup}
                onChange={(event) => setAllowsPickup(event.target.checked)}
              />
              Students board here (pickup)
            </label>
            <label className="flex items-center gap-2 text-sm text-ink">
              <input
                type="checkbox"
                checked={allowsDropOff}
                onChange={(event) => setAllowsDropOff(event.target.checked)}
              />
              Students alight here (drop-off)
            </label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <TextField
              label="Latitude"
              value={latitude}
              onChange={(event) => setLatitude(event.target.value)}
              hint="Optional. Recorded, not mapped."
            />
            <TextField
              label="Longitude"
              value={longitude}
              onChange={(event) => setLongitude(event.target.value)}
            />
          </div>
          <TextField
            label="Notes"
            value={stopNotes}
            onChange={(event) => setStopNotes(event.target.value)}
          />
        </div>
      </Modal>

      {/* ---- assign vehicle / driver / attendant ---------------------------- */}
      <Modal
        open={assigning !== null}
        onClose={closeAssign}
        title={assigning === null ? '' : SLOT_TITLE[assigning]}
        description="Closes the previous assignment rather than overwriting it."
        footer={
          <>
            <Button variant="secondary" onClick={closeAssign}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (assigning === 'vehicle') {
                  assignVehicle.mutate({
                    vehicleId: pickId,
                    from: pickFrom === '' ? undefined : pickFrom,
                  });
                } else {
                  assignStaff.mutate({
                    driverId: pickId,
                    from: pickFrom === '' ? undefined : pickFrom,
                  });
                }
              }}
              disabled={pickId === '' || assignVehicle.isPending || assignStaff.isPending}
            >
              Assign
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <TextField
            label="Search"
            value={pickSearch}
            onChange={(event) => setPickSearch(event.target.value)}
            placeholder={assigning === 'vehicle' ? 'Plate, fleet number, make…' : 'Name…'}
            hint="The list is one page of fifty active rows; narrowing happens on the server."
          />
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-[0.04em] text-muted">
              {assigning === 'vehicle' ? 'Vehicle' : assigning === 'attendant' ? 'Attendant' : 'Driver'}
            </span>
            <select
              className="mt-1 w-full border border-line bg-bg px-3 py-2 text-sm text-ink"
              value={pickId}
              onChange={(event) => setPickId(event.target.value)}
            >
              <option value="">Choose…</option>
              {assigning === 'vehicle'
                ? (vehicleOptions.data?.data ?? []).map((row) => (
                    <option key={row.id} value={row.id}>
                      {row.displayName} — {row.seatingCapacity} seats
                    </option>
                  ))
                : (staffOptions.data?.data ?? []).map((row) => (
                    <option key={row.id} value={row.id}>
                      {row.employeeName}
                      {row.tripEligible ? '' : ' — not eligible'}
                    </option>
                  ))}
            </select>
          </label>
          {pickedStaff !== undefined && pickedStaff.eligibilityBlockers.length > 0 && (
            <Blockers
              reasons={pickedStaff.eligibilityBlockers}
              title="The server will refuse this assignment"
            />
          )}
          <TextField
            label="Effective from"
            type="date"
            value={pickFrom}
            onChange={(event) => setPickFrom(event.target.value)}
            hint="Left blank, today."
          />
        </div>
      </Modal>

      {/* ---- end an assignment ---------------------------------------------- */}
      <Modal
        open={ending !== null}
        onClose={closeEnd}
        title={
          ending === 'vehicle'
            ? 'Take the vehicle off the route'
            : ending === 'attendant'
              ? 'Take the attendant off the route'
              : 'Take the driver off the route'
        }
        description="The assignment is closed, not deleted — it stays on the record."
        footer={
          <>
            <Button variant="secondary" onClick={closeEnd}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                const reason = endReason.trim() === '' ? undefined : endReason.trim();
                if (ending === 'vehicle') {
                  endVehicle.mutate(reason);
                } else {
                  endStaff.mutate({
                    role: ending === 'attendant' ? 'ATTENDANT' : 'DRIVER',
                    reason,
                  });
                }
              }}
              disabled={endVehicle.isPending || endStaff.isPending}
            >
              End it
            </Button>
          </>
        }
      >
        <TextField
          label="Reason"
          value={endReason}
          onChange={(event) => setEndReason(event.target.value)}
          hint="Optional, and worth a word — it is what the record shows later."
        />
      </Modal>
    </div>
  );
}

/**
 * One staffing panel: who serves the route in this slot, and the two actions.
 *
 * <p>Three of these sit under the details card. The open assignment comes off
 * the route record; earlier ones are closed and kept on the server, which has
 * no endpoint to read them back yet — so the panel shows the open state only
 * and says so where it cannot.
 */
function AssignmentPanel({
  title,
  current,
  note,
  canManage,
  assignLabel,
  onAssign,
  onEnd,
}: {
  title: string;
  current: ReactNode;
  note: string;
  canManage: boolean;
  assignLabel: string;
  onAssign: () => void;
  onEnd: () => void;
}) {
  return (
    <Card className="p-4">
      <h2 className="text-sm font-extrabold uppercase tracking-[0.05em] text-ink">{title}</h2>
      <div className="mt-2 text-sm text-ink">{current}</div>
      <p className="mt-1.5 text-xs text-muted">{note}</p>
      {canManage && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          <Button size="sm" variant="secondary" onClick={onAssign}>
            {assignLabel}
          </Button>
          <Button size="sm" variant="ghost" onClick={onEnd}>
            End assignment
          </Button>
        </div>
      )}
    </Card>
  );
}
